import { z } from 'zod';

/**
 * ADR-103 Amendment-6 — Inline-sign (create-then-sign a bérlés-wizard záró lépésében, Phase-2 b).
 *
 * **BE-flow (X' — thin createContract + a meglévő ADR-099 esign-stack reuse-a):**
 *
 * 1. `POST /api/v1/contracts/inline-sign/start` (`InlineSignStartSchema`) → **vékony `createContract`**
 *    (get-or-create a `rentalId`-n: `findByRentalId` ?? `generateNextNumber` + `repository.create`,
 *    ADR-103 Am-6 D-i) → `InlineSignStartResultSchema` (`contractId` + `contractNumber` + `status:'DRAFT'`).
 *    **Idempotens**: a dup-contract guard a `rentalId` FK-egyediségen (egy rentalId → egy contractId);
 *    retry ugyanazt a contractId-t adja vissza, NEM hoz létre duplikátot.
 * 2. `POST /api/v1/esign/session` (a **meglévő** ADR-099 endpoint) `{ sourceType:'RENTAL_CONTRACT',
 *    sourceId: contractId, level, provider }` → `signingSessionId`.
 * 3. `POST /api/v1/esign/session/:signingSessionId/sign` (a **meglévő** endpoint) `{ signatureData }`
 *    → `signedAt` + a SHIPPED post-sign handler (Am-5 D-5.3) automatikusan írja a `RentalContract.signedAt`-et.
 *
 * **Miért nem egy bundle-ölt Start-endpoint** (contract+session egyben)? Az `EsignService` az
 * `EsignModule.forRoot` mögött él, ami az `EsignController`-t is bundle-li → kétszeri import duplikálná
 * a `/esign/session` route-okat. A thin createContract (RentalModule) + a meglévő esign session/sign
 * reuse zero cross-module DI-rizikóval ad ADR-faithful flow-t (a session/sign már SHIPPED + tesztelt).
 *
 * Az `InlineSignComplete*`/`InlineSignResult*` a 3. lépés **logikai modellje** (a FE az esign-sign
 * válaszát + az ismert `contractId`-t mappeli erre).
 */

export const InlineSignStartSchema = z.object({
  rentalId: z.string().uuid(),
  /** default-template ha hiányzik (BE üresen hagyja — a PDF/template kötés később) */
  templateId: z.string().uuid().optional(),
  /** a wizard-session stabil kulcsa — wizard-szintű retry-guard (a BE-dedup a rentalId-n) */
  idempotencyKey: z.string().min(8).max(128),
});
export type InlineSignStart = z.infer<typeof InlineSignStartSchema>;

/**
 * A thin createContract (1. lépés) eredménye: szerződés get-or-create-elve, aláírásra vár.
 * A `signingSessionId`-t a FE a 2. lépésben (`POST /esign/session`) kapja meg — NEM ez adja.
 */
export const InlineSignStartResultSchema = z.object({
  contractId: z.string().uuid(),
  contractNumber: z.string(),
  status: z.literal('DRAFT'),
});
export type InlineSignStartResult = z.infer<typeof InlineSignStartResultSchema>;

/**
 * Aláírás-beküldés logikai modellje (3. lépés). A transport a meglévő
 * `POST /esign/session/:id/sign` `{ signatureData }` body-ja (a `sessionId` a path-ból jön).
 */
export const InlineSignCompleteSchema = z.object({
  signingSessionId: z.string().uuid(),
  signatureData: z.string(),
});
export type InlineSignComplete = z.infer<typeof InlineSignCompleteSchema>;

/**
 * A sikeres aláírás logikai eredménye. A FE az esign-sign válaszának `signedAt`-jét + az
 * 1. lépésből ismert `contractId`-t kombinálja ide.
 */
export const InlineSignResultSchema = z.object({
  contractId: z.string().uuid(),
  signedAt: z.string().datetime(),
  status: z.literal('SIGNED'),
});
export type InlineSignResult = z.infer<typeof InlineSignResultSchema>;
